import { MAGASINS, deciderFusion } from './sync.js';

/**
 * Export et import JSON - document 02, section 8. **Aucune entrée-sortie.**
 *
 * La fusion réutilise `deciderFusion` de la synchronisation : c'est la même
 * règle, « dernière écriture gagne par entité ». En écrire une seconde
 * garantirait qu'elles divergent, et un import ne se comporterait plus comme
 * une synchro.
 */

// Format 2 : ajout de la liste `movements`. Un fichier au format 1 reste
// lisible - ses mouvements sont simplement absents, ce qui est exact pour une
// sauvegarde antérieure au livre.
export const FORMAT = 2;

/** Document d'export. `settings` n'y figure jamais : il est propre à l'appareil. */
export function construireExport({ profiles = [], quotes = [], movements = [] }, { inclureSupprimes = false } = {}) {
  const garder = (e) => inclureSupprimes || !e.deleted;
  return {
    format: FORMAT,
    exportedAt: new Date().toISOString(),
    profiles: profiles.filter(garder),
    quotes: quotes.filter(garder),
    movements: movements.filter(garder),
  };
}

/**
 * Contrôle de recevabilité d'un fichier importé.
 *
 * Un import remplace ou fusionne des données réelles : mieux vaut refuser un
 * fichier douteux que corrompre le magasin. Le message dit ce qui cloche, pas
 * seulement que ça cloche.
 */
export function valider(doc) {
  if (!doc || typeof doc !== 'object') return 'Fichier illisible : objet JSON attendu.';
  if (!Array.isArray(doc.profiles) || !Array.isArray(doc.quotes)) {
    return 'Fichier invalide : les listes « profiles » et « quotes » sont attendues.';
  }
  // Tolérée absente : un export au format 1 est antérieur au livre. La refuser
  // rendrait illisibles des sauvegardes parfaitement valides.
  if (doc.movements !== undefined && !Array.isArray(doc.movements)) {
    return 'Fichier invalide : « movements » doit être une liste.';
  }
  if (doc.format !== undefined && doc.format > FORMAT) {
    return `Fichier au format ${doc.format}, produit par une version plus récente. Mettez l’application à jour avant d’importer.`;
  }
  const orphelin = [...doc.profiles, ...doc.quotes, ...(doc.movements ?? [])]
    .find((e) => !e?.id || !e?.updatedAt);
  if (orphelin) return 'Fichier invalide : une entrée est dépourvue d’identifiant ou d’horodatage.';
  return null;
}

/** Entités du document, étiquetées de leur type comme le veut l'arbitrage. */
export function entitesDe(doc) {
  return [
    ...doc.profiles.map((e) => ({ ...e, type: 'profile' })),
    ...doc.quotes.map((e) => ({ ...e, type: 'quote' })),
    ...(doc.movements ?? []).map((e) => ({ ...e, type: 'movement' })),
  ];
}

/**
 * Décide ce qu'un import doit écrire.
 *
 * - `fusionner` applique la même règle que la synchro : une entrée du fichier
 *   ne remplace la locale que si elle est plus récente.
 * - `remplacer` écrit tout, sans comparer : c'est l'intention explicite de
 *   l'utilisateur, et le seul mode qui permet de revenir en arrière après une
 *   fausse manœuvre.
 */
export function deciderImport(doc, locales, mode) {
  const entites = entitesDe(doc).filter((e) => MAGASINS[e.type]);
  if (mode === 'remplacer') return { aEcrire: entites, ignorees: [] };
  return deciderFusion(entites, locales);
}

/** Nom de fichier lisible et triable. */
export const nomFichier = (date = new Date()) =>
  `corridorfx-${date.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
